import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  FolderKanban, 
  MoreVertical, 
  Trash2, 
  ListTodo,
  Briefcase,
  Clock,
  Search,
  BarChart3,
  ArrowUpDown,
  LayoutGrid,
  List,
  ChevronDown
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

const Projects = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState({});
  const [newTask, setNewTask] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('lastActive');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list' - list is default
  const [hoveredProject, setHoveredProject] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const taskInputRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    fetchTimeEntries();
  }, [api]);

  const fetchProjects = async () => {
    if (!api) return;
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      
      if (response.data.length > 0) {
        const taskPromises = response.data.map(p => 
          api.get(`/tasks/project/${p.id}`).then(res => ({ id: p.id, tasks: res.data })).catch(() => ({ id: p.id, tasks: [] }))
        );
        const taskResults = await Promise.all(taskPromises);
        const taskMap = taskResults.reduce((acc, { id, tasks }) => {
          acc[id] = tasks;
          return acc;
        }, {});
        setTasks(taskMap);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    if (!api) return;
    try {
      const response = await api.get('/time-entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  // Calculate hours tracked per project
  const projectHours = useMemo(() => {
    const hours = {};
    timeEntries.forEach(entry => {
      if (entry.project_id && entry.duration) {
        hours[entry.project_id] = (hours[entry.project_id] || 0) + entry.duration;
      }
    });
    return hours;
  }, [timeEntries]);

  // Calculate hours per task
  const taskHours = useMemo(() => {
    const hours = {};
    timeEntries.forEach(entry => {
      if (entry.task_id && entry.duration) {
        hours[entry.task_id] = (hours[entry.task_id] || 0) + entry.duration;
      }
    });
    return hours;
  }, [timeEntries]);

  // Get last activity date for project
  const getLastActivity = (projectId) => {
    const projectEntries = timeEntries.filter(e => e.project_id === projectId);
    if (projectEntries.length === 0) return null;
    const sorted = projectEntries.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    return sorted[0]?.start_time;
  };

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let result = projects;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.client_name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }
    
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'nameAZ':
          return a.name.localeCompare(b.name);
        case 'mostHours':
          return (projectHours[b.id] || 0) - (projectHours[a.id] || 0);
        case 'lastActive':
          const lastA = getLastActivity(a.id);
          const lastB = getLastActivity(b.id);
          if (!lastA && !lastB) return 0;
          if (!lastA) return 1;
          if (!lastB) return -1;
          return new Date(lastB) - new Date(lastA);
        case 'recentlyAssigned':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });
    
    return result;
  }, [projects, searchQuery, sortBy, projectHours, timeEntries]);

  const formatHours = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatLastActivity = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (entryDate.getTime() === today.getTime()) return 'Today';
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const handleAddTask = async (projectId) => {
    if (!newTask.trim()) return;

    try {
      await api.post('/tasks', {
        name: newTask,
        project_id: projectId
      });
      toast.success('Task added');
      setNewTask('');
      fetchProjects();
      taskInputRef.current?.focus();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleDeleteTaskClick = (taskId) => {
    const hours = taskHours[taskId] || 0;
    if (hours > 0) {
      setTaskToDelete(taskId);
    } else {
      confirmDeleteTask(taskId);
    }
  };

  const confirmDeleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      setTaskToDelete(null);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'archived':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-800 dark:text-slate-400">Archived</span>;
      case 'on_hold':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">On Hold</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">Active</span>;
    }
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'nameAZ': return 'Project Name A→Z';
      case 'mostHours': return 'Most Hours Tracked';
      case 'lastActive': return 'Last Active';
      case 'recentlyAssigned': return 'Recently Assigned';
      default: return 'Last Active';
    }
  };

  // Grid Card View
  const renderProjectCard = (project) => {
    const hours = projectHours[project.id] || 0;
    const budgetHours = project.estimated_hours ? parseFloat(project.estimated_hours) * 3600 : null;
    const progressPercent = budgetHours ? Math.min(100, Math.round((hours / budgetHours) * 100)) : null;
    const lastActivity = getLastActivity(project.id);
    const isHovered = hoveredProject === project.id;
    const neverTracked = !lastActivity;
    
    return (
      <div 
        key={project.id} 
        className="premium-card cursor-pointer group relative overflow-hidden flex flex-col"
        style={{ borderTop: `4px solid ${project.color}` }}
        onClick={() => setSelectedProject(selectedProject === project.id ? null : project.id)}
        onMouseEnter={() => setHoveredProject(project.id)}
        onMouseLeave={() => setHoveredProject(null)}
        data-testid={`project-card-${project.id}`}
      >
        <div className="p-5 flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div 
                className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white truncate">
                    {project.name}
                  </h3>
                  {getStatusBadge(project.status || 'active')}
                </div>
                {/* Project description - max 1 line, truncated */}
                {project.description && (
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    {project.description}
                  </p>
                )}
              </div>
            </div>
            
            {/* Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 rounded-lg flex-shrink-0"
                  data-testid={`project-menu-${project.id}`}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl w-48">
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/reports', { state: { projectId: project.id } }); }}
                    className="rounded-lg"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View in Reports
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Client label */}
          {project.client_name && (
            <p className="text-xs mb-3 flex items-center gap-1">
              <span className="text-slate-400">Client:</span>
              <span className="text-slate-600 dark:text-slate-300 font-medium">{project.client_name}</span>
            </p>
          )}
          
          {/* Hours Progress Bar - Only show if budget exists */}
          <div className="mb-3 flex-1">
            {budgetHours ? (
              <>
                <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {formatHours(hours)} of {formatHours(budgetHours)}
                  </span>
                  <span className="text-xs font-semibold ml-auto" style={{ color: progressPercent > 90 ? '#ef4444' : project.color }}>
                    {progressPercent}%
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-300"
                    style={{ 
                      width: `${progressPercent}%`, 
                      backgroundColor: progressPercent > 90 ? '#ef4444' : project.color 
                    }}
                  />
                </div>
              </>
            ) : hours > 0 ? (
              <div className="flex items-center gap-1.5 text-slate-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">{formatHours(hours)} tracked</span>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No time tracked yet</p>
            )}
          </div>
          
          {/* Stats Row */}
          <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
            <div className="flex items-center gap-1.5">
              <ListTodo className="w-4 h-4" />
              <span className="font-medium">{tasks[project.id]?.length || 0} tasks</span>
            </div>
            <span className="text-slate-400">
              Last tracked {formatLastActivity(lastActivity)}
            </span>
          </div>
          
          {/* Expanded Task Section */}
          {selectedProject === project.id && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 animate-fade-in" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-3">
                <Input
                  ref={taskInputRef}
                  placeholder="Add a task..."
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTask(project.id);
                    }
                  }}
                  className="flex-1 h-9 text-sm rounded-lg"
                  data-testid={`add-task-input-${project.id}`}
                />
                <Button 
                  size="sm" 
                  onClick={() => handleAddTask(project.id)}
                  className="h-9 w-9 p-0 btn-premium rounded-lg"
                  data-testid={`add-task-btn-${project.id}`}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                {tasks[project.id]?.map(task => (
                  <div key={task.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 group/task">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{task.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 font-medium">
                        {formatHours(taskHours[task.id] || 0)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTaskClick(task.id)}
                        className="opacity-0 group-hover/task:opacity-100 h-6 w-6 p-0 rounded-md"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                {(!tasks[project.id] || tasks[project.id].length === 0) && (
                  <p className="text-xs text-slate-400 text-center py-3">No tasks yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // List View Row
  const renderProjectRow = (project) => {
    const hours = projectHours[project.id] || 0;
    const budgetHours = project.estimated_hours ? parseFloat(project.estimated_hours) * 3600 : null;
    const lastActivity = getLastActivity(project.id);
    
    return (
      <div 
        key={project.id}
        className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors cursor-pointer group"
        onClick={() => setSelectedProject(selectedProject === project.id ? null : project.id)}
        data-testid={`project-row-${project.id}`}
      >
        {/* Color dot */}
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: project.color }}
        />
        
        {/* Project Name + Description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {project.name}
            </h3>
            {getStatusBadge(project.status || 'active')}
          </div>
          {project.description && (
            <p className="text-xs text-slate-400 truncate mt-0.5">{project.description}</p>
          )}
        </div>
        
        {/* Client */}
        <div className="w-32 flex-shrink-0 hidden md:block">
          {project.client_name ? (
            <p className="text-xs">
              <span className="text-slate-400">Client: </span>
              <span className="text-slate-600 dark:text-slate-300">{project.client_name}</span>
            </p>
          ) : (
            <span className="text-xs text-slate-300">—</span>
          )}
        </div>
        
        {/* Hours */}
        <div className="w-24 flex-shrink-0 text-right">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {formatHours(hours)}
          </p>
          {budgetHours && (
            <p className="text-xs text-slate-400">of {formatHours(budgetHours)}</p>
          )}
        </div>
        
        {/* Last Tracked */}
        <div className="w-28 flex-shrink-0 text-right hidden sm:block">
          <p className="text-xs text-slate-400">
            {formatLastActivity(lastActivity)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('projects.title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('projects.subtitle')}</p>
          </div>
        </div>

        {/* Search, Sort, and View Toggle */}
        {projects.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search your projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 rounded-xl"
                data-testid="search-projects"
              />
            </div>
            
            <div className="flex items-center gap-2">
              {/* Sort Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="h-11 px-4 rounded-xl min-w-[180px] justify-between"
                    data-testid="sort-dropdown"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{getSortLabel()}</span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl w-52">
                  <DropdownMenuItem 
                    onClick={() => setSortBy('lastActive')}
                    className={`rounded-lg ${sortBy === 'lastActive' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' : ''}`}
                  >
                    Last Active
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setSortBy('nameAZ')}
                    className={`rounded-lg ${sortBy === 'nameAZ' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' : ''}`}
                  >
                    Project Name A→Z
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setSortBy('mostHours')}
                    className={`rounded-lg ${sortBy === 'mostHours' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' : ''}`}
                  >
                    Most Hours Tracked
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setSortBy('recentlyAssigned')}
                    className={`rounded-lg ${sortBy === 'recentlyAssigned' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' : ''}`}
                  >
                    Recently Assigned
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* View Toggle */}
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`h-11 w-11 flex items-center justify-center transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' 
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                  data-testid="view-grid"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`h-11 w-11 flex items-center justify-center transition-colors border-l border-slate-200 dark:border-slate-700 ${
                    viewMode === 'list' 
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400' 
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                  data-testid="view-list"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Projects Grid View - 2 columns on desktop, 1 on mobile */}
        {filteredProjects.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="projects-grid">
            {filteredProjects.map(renderProjectCard)}
          </div>
        )}
        
        {/* Projects List View */}
        {filteredProjects.length > 0 && viewMode === 'list' && (
          <div className="premium-card divide-y divide-slate-100 dark:divide-slate-800" data-testid="projects-list">
            {/* List Header */}
            <div className="flex items-center gap-4 px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              <div className="w-3 flex-shrink-0" />
              <div className="flex-1">Project</div>
              <div className="w-32 flex-shrink-0 hidden md:block">Client</div>
              <div className="w-24 flex-shrink-0 text-right">Hours</div>
              <div className="w-28 flex-shrink-0 text-right hidden sm:block">Last Tracked</div>
              <div className="w-20 flex-shrink-0" />
            </div>
            {filteredProjects.map(renderProjectRow)}
          </div>
        )}
        
        {/* Empty State */}
        {projects.length === 0 && !loading && (
          <div className="premium-card">
            <div className="empty-state">
              <div className="empty-state-icon">
                <FolderKanban className="w-10 h-10" />
              </div>
              <h3 className="empty-state-title">No projects assigned yet</h3>
              <p className="empty-state-text">
                Your manager will assign projects to you.
                <br />
                Once assigned, you'll be able to track time against them here.
              </p>
            </div>
          </div>
        )}

        {/* No Search Results */}
        {projects.length > 0 && filteredProjects.length === 0 && searchQuery && (
          <div className="premium-card">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Search className="w-10 h-10" />
              </div>
              <h3 className="empty-state-title">No projects found</h3>
              <p className="empty-state-text">
                No projects match "{searchQuery}"
              </p>
              <Button 
                variant="outline"
                onClick={() => setSearchQuery('')}
                className="mt-2"
              >
                Clear search
              </Button>
            </div>
          </div>
        )}

        {/* Delete Task Confirmation Dialog */}
        <AlertDialog open={!!taskToDelete} onOpenChange={(open) => !open && setTaskToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete task with logged time?</AlertDialogTitle>
              <AlertDialogDescription>
                This task already has <strong>{formatHours(taskHours[taskToDelete] || 0)}</strong> of logged time. Deleting it will permanently remove the task. Time entries will remain but will no longer be associated with this task.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-task-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => confirmDeleteTask(taskToDelete)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-task-btn"
              >
                Delete Task
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Projects;
